import React, { useState, useEffect } from "react";
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  useMediaQuery,
  useTheme,
  Collapse,
  Chip,
  Tooltip,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Business as PlantIcon,
  Settings as ProcessIcon,
  Room as RoomIcon,
  PrecisionManufacturing as MachineIcon,
  FolderOpen as CatalogIcon,
  People as UsersIcon,
  AccountCircle as ProfileIcon,
  Logout as LogoutIcon,
  ChevronLeft as ChevronLeftIcon,
  ExpandLess,
  ExpandMore,
  Home as HomeIcon,
  Search as SearchIcon,
  Settings as SettingsIcon,
  Security as SecurityIcon,
  RestoreFromTrash as TrashIcon,
  CloudUpload as BulkUploadIcon,
  Schedule as ScheduleIcon,
  Assessment as AssessmentIcon,
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { useHierarchy } from "../../hooks/useHierarchy";
import useGlobalSearch from "../../hooks/useGlobalSearch";
import GlobalSearch from "../common/GlobalSearch";

const DRAWER_WIDTH = 280;
const DRAWER_WIDTH_COLLAPSED = 64;

/**
 * Componente Layout principal de la aplicación
 * Proporciona la estructura base con AppBar, Drawer y contenido principal
 */
const Layout = ({ children }) => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Estados locales
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [drawerCollapsed, setDrawerCollapsed] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [hierarchyExpanded, setHierarchyExpanded] = useState(true);
  const [planningExpanded, setPlanningExpanded] = useState(false);
  const [adminExpanded, setAdminExpanded] = useState(false);

  // Hooks personalizados
  const { user, logout, isAdmin } = useAuth();
  const { getBreadcrumbPath, getCurrentLevel } = useHierarchy();
  const { searchTerm, setSearchTerm, results, loading, showResults, setShowResults, navigateToResult, clearSearch } =
    useGlobalSearch();

  // Configuración responsive
  useEffect(() => {
    if (isMobile) {
      setDrawerOpen(false);
      setDrawerCollapsed(false);
    } else {
      setDrawerOpen(true);
    }
  }, [isMobile]);

  // Navegación del menú
  const menuItems = [
    {
      id: "dashboard",
      label: "Dashboard",
      icon: <DashboardIcon />,
      path: "/dashboard",
      roles: ["admin", "operator", "viewer"],
    },
    {
      id: "hierarchy",
      label: "Jerarquía",
      icon: <HomeIcon />,
      expandable: true,
      roles: ["admin", "operator", "viewer"],
      children: [
        {
          id: "plants",
          label: "Plantas",
          icon: <PlantIcon />,
          path: "/plants",
          roles: ["admin", "operator", "viewer"],
        },
        {
          id: "processes",
          label: "Procesos",
          icon: <ProcessIcon />,
          path: "/processes",
          roles: ["admin", "operator", "viewer"],
        },
        {
          id: "rooms",
          label: "Salas",
          icon: <RoomIcon />,
          path: "/rooms",
          roles: ["admin", "operator", "viewer"],
        },
        {
          id: "machines",
          label: "Máquinas",
          icon: <MachineIcon />,
          path: "/machines",
          roles: ["admin", "operator", "viewer"],
        },
        {
          id: "catalogs",
          label: "Catálogos",
          icon: <CatalogIcon />,
          path: "/catalogs",
          roles: ["admin", "operator", "viewer"],
        },
      ],
    },
    {
      id: "planning",
      label: "Planificación",
      icon: <ScheduleIcon />,
      expandable: true,
      roles: ["admin", "operator", "viewer"],
      children: [
        {
          id: "shifts",
          label: "Turnos",
          icon: <ScheduleIcon />,
          path: "/shifts",
          roles: ["admin", "operator", "viewer"],
        },
        {
          id: "reports",
          label: "Reportes",
          icon: <AssessmentIcon />,
          path: "/reports",
          roles: ["admin", "operator", "viewer"],
        },
      ],
    },
    {
      id: "admin",
      label: "Administración",
      icon: <UsersIcon />,
      expandable: true,
      roles: ["admin"],
      children: [
        {
          id: "users",
          label: "Usuarios",
          icon: <UsersIcon />,
          path: "/users",
          roles: ["admin"],
        },
        {
          id: "bulk-upload",
          label: "Importación Masiva",
          icon: <BulkUploadIcon />,
          path: "/bulk-upload",
          roles: ["admin"],
        },
        {
          id: "trash",
          label: "Papelera",
          icon: <TrashIcon />,
          path: "/trash",
          roles: ["admin"],
        },
        {
          id: "tokens",
          label: "Tokens de Eliminación",
          icon: <SecurityIcon />,
          path: "/tokens",
          roles: ["admin"],
        },
        {
          id: "settings",
          label: "Configuración",
          icon: <SettingsIcon />,
          path: "/settings",
          roles: ["admin"],
        },
      ],
    },
  ];

  // Handlers
  const handleDrawerToggle = () => {
    if (isMobile) {
      setDrawerOpen(!drawerOpen);
    } else {
      setDrawerCollapsed(!drawerCollapsed);
    }
  };

  const handleUserMenuOpen = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile) {
      setDrawerOpen(false);
    }
  };

  const handleLogout = async () => {
    handleUserMenuClose();
    await logout();
    navigate("/login");
  };

  const isItemActive = (path) => {
    return location.pathname === path;
  };

  const hasPermission = (roles) => {
    return roles.includes(user?.role);
  };

  // Obtener título de página actual
  const getPageTitle = () => {
    const breadcrumb = getBreadcrumbPath();
    if (breadcrumb.length > 0) {
      return breadcrumb[breadcrumb.length - 1].name;
    }

    const currentPath = location.pathname;
    const menuItem = menuItems.flatMap((item) => item.children || [item]).find((item) => item.path === currentPath);

    return menuItem?.label || "SGCM";
  };

  // Renderizar elemento del menú
  const renderMenuItem = (item, level = 0) => {
    if (!hasPermission(item.roles)) return null;

    const isActive = item.path && isItemActive(item.path);
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded =
      item.id === "hierarchy"
        ? hierarchyExpanded
        : item.id === "planning"
        ? planningExpanded
        : item.id === "admin"
        ? adminExpanded
        : false;

    return (
      <React.Fragment key={item.id}>
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => {
              if (hasChildren) {
                if (item.id === "hierarchy") {
                  setHierarchyExpanded(!hierarchyExpanded);
                } else if (item.id === "planning") {
                  setPlanningExpanded(!planningExpanded);
                } else if (item.id === "admin") {
                  setAdminExpanded(!adminExpanded);
                }
              } else if (item.path) {
                handleNavigation(item.path);
              }
            }}
            selected={isActive}
            sx={{
              pl: level * 2 + 2,
              minHeight: 48,
              borderRadius: 1,
              mx: 1,
              mb: 0.5,
              "&.Mui-selected": {
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                "&:hover": {
                  backgroundColor: theme.palette.primary.dark,
                },
              },
            }}>
            <ListItemIcon
              sx={{
                minWidth: drawerCollapsed ? 0 : 40,
                mr: drawerCollapsed ? 0 : 1,
                color: isActive ? "inherit" : theme.palette.text.secondary,
              }}>
              {drawerCollapsed ? (
                <Tooltip title={item.label} placement="right">
                  {item.icon}
                </Tooltip>
              ) : (
                item.icon
              )}
            </ListItemIcon>
            {!drawerCollapsed && (
              <>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontSize: "0.875rem",
                    fontWeight: isActive ? 600 : 400,
                  }}
                />
                {hasChildren && (isExpanded ? <ExpandLess /> : <ExpandMore />)}
              </>
            )}
          </ListItemButton>
        </ListItem>

        {/* Submenu colapsable */}
        {hasChildren && !drawerCollapsed && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children.map((child) => renderMenuItem(child, level + 1))}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  // Contenido del drawer
  const drawerContent = (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Header del drawer */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: drawerCollapsed ? "center" : "space-between",
          p: 2,
          minHeight: 64,
        }}>
        {!drawerCollapsed && (
          <Typography variant="h6" color="primary" sx={{ fontWeight: 700 }}>
            SGCM
          </Typography>
        )}
        <IconButton
          onClick={handleDrawerToggle}
          size="small"
          sx={{
            color: theme.palette.primary.main,
          }}>
          {drawerCollapsed ? <MenuIcon /> : <ChevronLeftIcon />}
        </IconButton>
      </Box>

      <Divider />

      {/* Información del usuario */}
      {!drawerCollapsed && (
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <Avatar
              sx={{
                width: 40,
                height: 40,
                bgcolor: theme.palette.primary.main,
                mr: 2,
              }}>
              {user?.name?.charAt(0).toUpperCase()}
            </Avatar>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography variant="subtitle2" noWrap>
                {user?.name}
              </Typography>
              <Chip
                label={
                  user?.role === "admin" ? "Administrador" : user?.role === "operator" ? "Operador" : "Visualizador"
                }
                size="small"
                color={user?.role === "admin" ? "primary" : user?.role === "operator" ? "secondary" : "default"}
                sx={{ fontSize: "0.75rem" }}
              />
            </Box>
          </Box>
        </Box>
      )}

      <Divider />

      {/* Lista del menú */}
      <Box sx={{ flexGrow: 1, overflowY: "auto", py: 1 }}>
        <List>{menuItems.map((item) => renderMenuItem(item))}</List>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      {/* AppBar */}
      <AppBar
        position="fixed"
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          boxShadow: theme.shadows[1],
        }}>
        <Toolbar>
          <IconButton color="inherit" edge="start" onClick={handleDrawerToggle} sx={{ mr: 2, display: { md: "none" } }}>
            <MenuIcon />
          </IconButton>

          <Typography variant="h6" noWrap sx={{ flexGrow: 1, fontWeight: 600 }}>
            {getPageTitle()}
          </Typography>

          {/* Búsqueda Global */}
          <Box sx={{ display: { xs: "none", sm: "block" }, mr: 2 }}>
            <GlobalSearch
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Buscar en el sistema..."
              results={results}
              loading={loading}
              showResults={showResults}
              onResultClick={navigateToResult}
              onClose={() => setShowResults(false)}
            />
          </Box>

          {/* Menú de usuario */}
          <IconButton size="large" onClick={handleUserMenuOpen} color="inherit">
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: theme.palette.primary.main,
              }}>
              {user?.name?.charAt(0).toUpperCase()}
            </Avatar>
          </IconButton>

          <Menu
            anchorEl={userMenuAnchor}
            open={Boolean(userMenuAnchor)}
            onClose={handleUserMenuClose}
            onClick={handleUserMenuClose}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}>
            <MenuItem onClick={() => handleNavigation("/profile")}>
              <ListItemIcon>
                <ProfileIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Mi Perfil</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Cerrar Sesión</ListItemText>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Drawer */}
      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          width: drawerCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH,
            boxSizing: "border-box",
            transition: theme.transitions.create("width", {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
            overflowX: "hidden",
          },
        }}>
        {drawerContent}
      </Drawer>

      {/* Contenido principal */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          mt: 8,
          backgroundColor: theme.palette.background.default,
          minHeight: "calc(100vh - 64px)",
        }}>
        {children}
      </Box>
    </Box>
  );
};

export default Layout;
